/**
 * Step 4: Lease Terms
 * Configure lease type, dates, and auto-renewal
 */

import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { WizardState } from '../../../types/lease-generation';
import { RequiredMark } from '../RequiredMark';
import { listLeases, updateLease, ApiClientError } from '../../../lib/api';
import type { LeaseListItem } from '../../../types/lease';
import { Button } from '../../../components/ui/button';
import { LocalRuleNote } from '../../../components/LocalRuleNote';
import { formatLocalDate } from '../../../utils/date';
import { todayInTimezone } from '@resida/shared';
import { findUnitConflict } from './unit-conflict';

interface Step4Props {
  state: WizardState;
  updateInputs: (partial: Partial<WizardState['inputs']>) => void;
  unitId?: string;
  onConflictChange?: (hasConflict: boolean) => void;
  /** IANA timezone string for the property, resolved by unit ID via useUnitTimezone(). */
  timezone?: string;
}

/** Format a Date object as YYYY-MM-DD without UTC shift */
function formatDateYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Get the first day of the next month as YYYY-MM-DD, anchored to the property timezone */
function getFirstOfNextMonth(timezone: string): string {
  const todayStr = todayInTimezone(timezone); // YYYY-MM-DD
  const parts = todayStr.split('-').map(Number);
  const next = new Date(parts[0]!, parts[1]!, 1); // first day of next month (month is 1-indexed here)
  return formatDateYMD(next);
}

/** Add months to a date string and return YYYY-MM-DD (day before = end of term) */
function addMonths(dateStr: string, months: number): string {
  const parts = dateStr.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1, parts[2]!);
  d.setMonth(d.getMonth() + months);
  d.setDate(d.getDate() - 1); // end date is last day of the term
  return formatDateYMD(d);
}


const DURATION_OPTIONS = [
  { label: '6 months', months: 6 },
  { label: '1 year', months: 12 },
  { label: '2 years', months: 24 },
];

export function Step4LeaseTerms({ state, updateInputs, unitId, onConflictChange, timezone = 'America/New_York' }: Step4Props) {
  const { inputs, jurisdictionInfo } = state;

  const [unitConflictLease, setUnitConflictLease] = useState<LeaseListItem | null>(null);
  const [unitConflictResolving, setUnitConflictResolving] = useState(false);
  const [conflictEndDate, setConflictEndDate] = useState<string>('');
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);

  // Track the last checked startDate to avoid redundant checks
  const lastCheckedStartDate = useRef<string | null>(null);

  // Auto-set startDate to first of next month on first visit
  useEffect(() => {
    if (!inputs.startDate) {
      updateInputs({ startDate: getFirstOfNextMonth(timezone) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for unit conflict when startDate is set
  useEffect(() => {
    const startDate = inputs.startDate;
    if (!startDate || !unitId || startDate === lastCheckedStartDate.current) return;

    lastCheckedStartDate.current = startDate;
    setIsCheckingConflict(true);
    setConflictError(null);

    listLeases({ unitId })
      .then((result) => {
        // BR-LL-01: only a lease still running on the new start date blocks (see findUnitConflict).
        const conflict = findUnitConflict(result.leases, startDate);
        if (conflict) {
          setUnitConflictLease(conflict);
          onConflictChange?.(true);
        } else {
          setUnitConflictLease(null);
          onConflictChange?.(false);
        }
      })
      .catch(() => {
        // Ignore conflict check errors — don't block wizard for transient failures
        setUnitConflictLease(null);
        onConflictChange?.(false);
      })
      .finally(() => {
        setIsCheckingConflict(false);
      });
  }, [inputs.startDate, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetConflictEndDate = async () => {
    if (!unitConflictLease || !conflictEndDate) return;
    try {
      setUnitConflictResolving(true);
      setConflictError(null);
      await updateLease('', unitConflictLease.id, { endDate: conflictEndDate });
      setUnitConflictLease(null);
      onConflictChange?.(false);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setConflictError(err.message);
      } else {
        setConflictError('Failed to update lease end date');
      }
    } finally {
      setUnitConflictResolving(false);
    }
  };

  const leaseType = inputs.leaseType ?? 'fixed-term';
  const startDate = inputs.startDate ?? '';
  const endDate = inputs.endDate ?? '';
  const autoRenewal = inputs.autoRenewal ?? false;

  const terminationDays = jurisdictionInfo?.rules.notice_periods?.termination_month_to_month_landlord_days;

  /** Which duration option matches the current start/end dates? */
  const activeDuration = startDate && endDate
    ? DURATION_OPTIONS.find(opt => addMonths(startDate, opt.months) === endDate)
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Lease Terms</h3>
        <p className="text-sm text-muted-foreground">Configure the type and duration of this lease.</p>
      </div>

      {/* Unit Conflict Banner */}
      {isCheckingConflict && (
        <div className="p-3 bg-accent/50 border border-border rounded-lg text-sm text-muted-foreground">
          Checking for existing leases on this unit...
        </div>
      )}

      {unitConflictLease && !isCheckingConflict && (
        <div data-testid="unit-conflict-banner" className="flex flex-col gap-3 p-4 bg-destructive/5 border border-destructive/30 rounded-lg">
          <div>
            <h4 className="text-sm font-semibold text-destructive mb-1">Unit Conflict</h4>
            <p className="text-sm text-destructive">
              Unit already has an active lease. Set an end date on the existing lease before creating a new one.
            </p>
          </div>

          <div className="bg-card rounded-md border border-border p-3 text-sm">
            <div className="flex justify-between gap-2 mb-1">
              <span className="text-muted-foreground">Existing lease period:</span>
              <span className="text-foreground font-medium">
                {formatLocalDate(unitConflictLease.startDate)} — {unitConflictLease.endDate ? formatLocalDate(unitConflictLease.endDate) : 'month-to-month'}
              </span>
            </div>
          </div>

          {conflictError && (
            <div className="p-2 bg-destructive/5 border border-destructive/20 rounded text-sm text-destructive">
              {conflictError}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label htmlFor="conflict-end-date" className="text-sm font-medium text-foreground">
              Set end date for existing lease
            </label>
            <input
              id="conflict-end-date"
              type="date"
              data-testid="unit-conflict-end-date-picker"
              value={conflictEndDate}
              onChange={(e) => setConflictEndDate(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button
              type="button"
              data-testid="unit-conflict-set-end-date"
              onClick={handleSetConflictEndDate}
              disabled={unitConflictResolving || !conflictEndDate}
              variant="destructive"
              className="min-h-[44px]"
            >
              {unitConflictResolving ? 'Setting end date...' : 'Set End Date on Existing Lease'}
            </Button>
            <p className="text-xs text-muted-foreground">
              This will set the end date on the existing active lease so the new lease can be created.
            </p>
            <Link
              to={`/leases/${unitConflictLease.id}`}
              className="text-sm text-primary hover:text-primary/80 no-underline"
            >
              View existing lease →
            </Link>
          </div>
        </div>
      )}

      {/* Lease Type */}
      <div>
        <label className="block mb-2 text-sm font-medium text-foreground">Lease Type <RequiredMark /></label>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="lease-type-fixed"
            onClick={() => updateInputs({ leaseType: 'fixed-term' })}
            className={`flex-1 px-4 py-3 text-sm font-medium border rounded-lg min-h-[44px] ${
              leaseType === 'fixed-term'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-foreground border-border hover:bg-accent'
            }`}
          >
            Fixed-term
          </button>
          <button
            type="button"
            data-testid="lease-type-month"
            onClick={() => updateInputs({ leaseType: 'month-to-month', endDate: undefined })}
            className={`flex-1 px-4 py-3 text-sm font-medium border rounded-lg min-h-[44px] ${
              leaseType === 'month-to-month'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-foreground border-border hover:bg-accent'
            }`}
          >
            Month-to-month
          </button>
        </div>
        {leaseType === 'month-to-month' && terminationDays != null && (
          <p className="mt-2 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
            {jurisdictionInfo?.displayName} requires {terminationDays}-day notice for month-to-month termination.
          </p>
        )}
        <LocalRuleNote coverage={jurisdictionInfo?.coverage} category="termination_notice" />
      </div>

      {/* Start Date */}
      <div>
        <label htmlFor="start-date" className="block mb-1 text-sm font-medium text-foreground">
          Start Date <RequiredMark />
        </label>
        <input
          id="start-date"
          type="date"
          data-testid="start-date"
          value={startDate}
          onChange={e => updateInputs({ startDate: e.target.value })}
          className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          required
        />
      </div>

      {/* Duration buttons + End Date (fixed-term only) */}
      {leaseType === 'fixed-term' && (
        <>
          <div>
            <label className="block mb-2 text-sm font-medium text-foreground">Duration</label>
            <div className="flex gap-3">
              {DURATION_OPTIONS.map(opt => (
                <button
                  key={opt.months}
                  type="button"
                  data-testid={`duration-${opt.months}`}
                  disabled={!startDate}
                  onClick={() => updateInputs({ endDate: addMonths(startDate, opt.months) })}
                  className={`flex-1 px-3 py-2.5 text-sm font-medium border rounded-lg min-h-[44px] ${
                    activeDuration?.months === opt.months
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-foreground border-border hover:bg-accent disabled:opacity-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="end-date" className="block mb-1 text-sm font-medium text-foreground">
              End Date <RequiredMark />
            </label>
            <input
              id="end-date"
              type="date"
              data-testid="end-date"
              value={endDate}
              min={startDate || undefined}
              onChange={e => updateInputs({ endDate: e.target.value })}
              className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
        </>
      )}

      {/* Auto-renewal (fixed-term only) */}
      {leaseType === 'fixed-term' && (
        <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
          <label className="flex items-center gap-3 cursor-pointer flex-1">
            <input
              type="checkbox"
              checked={autoRenewal}
              onChange={e => updateInputs({ autoRenewal: e.target.checked })}
              className="w-4 h-4 text-primary border-input rounded focus:ring-ring"
            />
            <div>
              <div className="text-sm font-medium text-foreground">Convert to month-to-month</div>
              <div className="text-xs text-muted-foreground">
                Automatically convert to a month-to-month tenancy after the fixed term ends
              </div>
            </div>
          </label>
        </div>
      )}
    </div>
  );
}
